import {
  Button,
  ListRow,
  ListRows,
  SearchInput,
  Skeleton,
  formatPhone,
  titleCase,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import { usePatientSearch, type PatientContact } from './api'

/**
 * A escolha da paciente em "+ Enviar anamnese" na lista global (RN-ANA-01): a emissão parte sempre
 * de uma paciente. Busca no cadastro da clínica (`GET /patients?search=`) e segue para o mesmo
 * formulário da ficha.
 */

const lineClass = 'font-body text-secondary text-[13px] leading-[1.6] font-light'

function contactLine(patient: PatientContact) {
  const parts = [patient.whatsapp ? formatPhone(patient.whatsapp) : null, patient.email].filter(
    Boolean,
  )
  return parts.length > 0 ? parts.join(' · ') : 'Sem WhatsApp nem e-mail no cadastro'
}

export function PatientPicker({
  onSelect,
  onClose,
}: {
  onSelect: (patient: PatientContact) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const term = useDebounced(search.trim())
  const query = usePatientSearch(term)
  const patients = query.data?.data ?? []
  const total = query.data?.meta.total ?? 0

  return (
    <div className="flex flex-col gap-4">
      <SearchInput
        autoFocus
        value={search}
        onChange={setSearch}
        placeholder="Buscar paciente por nome, CPF ou telefone"
        aria-label="Buscar paciente"
      />

      {term.length < 2 ? (
        <p className={lineClass}>
          Digite pelo menos 2 letras do nome. A anamnese parte sempre de uma paciente cadastrada.
        </p>
      ) : query.isError ? (
        <LoadError
          title="Não conseguimos buscar as pacientes"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <div role="status" aria-label="Buscando pacientes" className="flex flex-col gap-2">
          <Skeleton className="h-[48px] w-full" />
          <Skeleton className="h-[48px] w-full" />
        </div>
      ) : patients.length === 0 ? (
        <div>
          <p className={lineClass}>
            Nenhuma paciente para "{term}". A busca cobre nome, CPF, e-mail e telefone.
          </p>
          <Button variant="outline" className="mt-3" asChild>
            <Link to={routes.newPatient}>Criar paciente</Link>
          </Button>
        </div>
      ) : (
        <div>
          <ListRows role="group" aria-label="Pacientes encontradas">
            {patients.map((patient) => (
              <ListRow
                key={patient.id}
                title={titleCase(patient.name)}
                meta={contactLine(patient)}
                aria-label={`Escolher ${titleCase(patient.name)}`}
                onClick={() => onSelect(patient)}
              />
            ))}
          </ListRows>
          {total > patients.length ? (
            <p className="font-body text-eyebrow text-label mt-2 leading-[1.5] font-light tracking-normal">
              Mostrando {patients.length} de {total}. Digite mais do nome para chegar à paciente.
            </p>
          ) : null}
        </div>
      )}

      <div>
        <Button variant="text" onClick={onClose}>
          Desistir do envio
        </Button>
      </div>
    </div>
  )
}
